import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, inject, OnInit, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { QUESTION_PROTECTION } from '../../../../core/constants/question-solutions';
import { IQuestion } from '../../../../core/models/question.model';
import { OnlyNumberDirective } from '../../../../shared/directives/only-number.directive';
import { SolutionService } from '../../service/solution.service';
import { finalize } from 'rxjs';
import { CommonService } from '../../../../shared/services/common.service';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { NgxCurrencyDirective } from 'ngx-currency';
@Component({
  selector: 'app-form-protection',
  templateUrl: './form-protection.component.html',
  styleUrls: ['./form-protection.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    BaseChartDirective,
    OnlyNumberDirective,
    NgxCurrencyDirective,
  ],
  standalone: true,
})
export class FormProtectionComponent implements OnInit {
  stepIndex: number = 1;
  formProtection: FormGroup = new FormGroup({});
  amountCalculate: number = 0;
  showResult: boolean = false;
  private solutionService = inject(SolutionService);
  private commonService = inject(CommonService);
  @Output() changeStepEvent = new EventEmitter<boolean>();
  @Output() hideLogoEvent = new EventEmitter<boolean>();
  public chartType: ChartType = 'pie';
  public chartData: ChartConfiguration['data'] = {
    labels: [
      'Vivienda',
      'Alimentación',
      'Educación',
      'Transporte',
      'Servicios varios',
      'Ocio',
      'Ahorro y protección',
      'Otros',
    ],
    datasets: [
      {
        label: 'Porcentaje: ',
        data: [25, 20, 15, 5, 7.5, 7.5, 15, 5],
        backgroundColor: [
          '#688A54',
          '#8A5A54',
          '#546A8A',
          '#484A40',
          '#77548A',
          '#54878A',
          '#FFB9B0',
          '#EEFFB0',
        ],
      },
    ],
  };
  public chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'right',
        labels: {
          padding: 20,
          boxWidth: 20,
          font: {
            size: 16,
            weight: 'lighter',
            family: 'Montserrat-light',
          },
          color: '#0A3245',
        },
      },
      datalabels: {
        formatter: (value: number) => value + '%',
        color: '#fff',
        font: {
          weight: 'bold',
          family: 'Montserrat-bold',
          size: 14,
        },
      },
    },
  };

  constructor(private dialog: MatDialog) {}

  ngOnInit() {
    this.setForm();
  }

  ngDoCheck() {
    this.updateLegendPosition();
  }

  setForm() {
    this.formProtection = new FormGroup({
      name: new FormControl('', [Validators.required]),
      age: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      country: new FormControl('', [Validators.required]),
      peopleDepend: new FormControl('', [Validators.required]),
      amountHeritage: new FormControl('', [Validators.required]),
      salaryMonthly: new FormControl('', [Validators.required]),
      amountFamily: new FormControl('', [Validators.required]),
    });
  }

  backStep() {
    if (this.stepIndex === 2) {
      this.stepIndex--;
    } else if (this.stepIndex === 1) {
      this.changeStepEvent.emit(true);
    }
  }

  nextStep(): void {
    this.stepIndex++;
    if (this.stepIndex === 3) {
      this.saveForm();
    }
  }

  saveForm(contactar: boolean = false): void {
    if (!this.formProtection.valid) {
      return;
    }
    let form: IQuestion[] = [];
    QUESTION_PROTECTION.forEach((item) => {
      if (this.formProtection?.value[item.value]) {
        form.push({
          question: item.question,
          answer: this.formProtection?.value[item.value],
        });
      }
    });
    this.commonService.loading.next(true);
    const payload = {
      tipo: 'Protección de patrimonio',
      emailClient: this.formProtection?.value.email,
      contactar: contactar,
      form: form,
    };
    this.hideLogoEvent.emit(true);
    this.solutionService
      .sendForm(payload)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.showResult = true;
            this.commonService.loading.next(false);
          }, 6000);
          if (contactar) {
            this.commonService.loading.next(false);
            this.formProtection.reset();
            this.setForm();
          }
        })
      )
      .subscribe((response) => {
        if (response) {
          if (!contactar) {
            let amountCalculate =
              ((+this.formProtection?.value.amountFamily * 12) / 0.07 -
                +this.formProtection?.value.amountHeritage) *
              (1 + 0.1 * +this.formProtection?.value.peopleDepend);
            this.amountCalculate = +amountCalculate.toFixed(0);
          } else {
            let dialogRef = this.dialog.open(AlertComponent, {
              width: '536px',
              data: {
                buttonText: 'Volver a calcular',
              },
            });
            dialogRef.afterClosed().subscribe((result) => {
              this.resetForm();
            });
          }
        }
      });
  }

  validateteStep(): boolean {
    let disabled: boolean = false;
    switch (this.stepIndex) {
      case 1:
        disabled =
          !this.formProtection?.get('name')?.valid ||
          !this.formProtection?.get('age')?.valid ||
          !this.formProtection?.get('email')?.valid ||
          !this.formProtection?.get('country')?.valid;
        break;
      case 2:
        disabled =
          !this.formProtection?.get('peopleDepend')?.valid ||
          !this.formProtection?.get('amountHeritage')?.valid ||
          !this.formProtection?.get('salaryMonthly')?.valid ||
          !this.formProtection?.get('amountFamily')?.valid;
        break;
    }
    return disabled;
  }

  resetForm(): void {
    this.stepIndex = 1;
    this.formProtection.reset();
    this.setForm();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.updateLegendPosition();
  }

  updateLegendPosition() {
    const width = window.innerWidth;
    if (width <= 920) {
      this.chartOptions!.plugins!.legend!.position = 'bottom';
      this.chartOptions!.plugins!.legend!.labels! = {
        padding: 10,
        boxWidth: 10,
        font: {
          size: 14,
          weight: 'lighter',
          family: 'Montserrat-light',
        },
        color: '#0A3245',
      }
    } else {
      this.chartOptions!.plugins!.legend!.position = 'right';
    }
  }

  get backgroundColors(): string[] {
    return this.chartData.datasets[0]?.backgroundColor as string[] || [];
  }

}
